import { Award, Gift, Loader2, Pencil, Trash2, Trophy, Users } from 'lucide-react'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Card, CardContent } from '../ui/card'
import { formatWinnerNames, getPrizeWinners } from '../../lib/prizeWinners'

export default function PrizeList({
  prizes,
  loading,
  onEdit,
  onDelete,
  onAssignWinner,
}) {
  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    )
  }

  if (prizes.length === 0) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center">
            <Gift className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="text-muted-foreground">No prizes found</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="grid gap-4">
      {prizes.map((prize) => {
        const winners = getPrizeWinners(prize)
        const hasWinner = winners.length > 0
        return (
        <Card key={prize.id}>
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col gap-4">
              {/* Prize Info */}
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <h3 className="text-lg font-semibold">{prize.name}</h3>
                  <Badge variant="outline">{prize.place === 1 ? '1st' : prize.place === 2 ? '2nd' : prize.place === 3 ? '3rd' : `${prize.place}th`} Place</Badge>
                  {!prize.group && (
                    <Badge variant="secondary">
                      <Trophy className="h-3 w-3 mr-1" />
                      Global
                    </Badge>
                  )}
                  {hasWinner && (
                    <Badge variant="teal">
                      <Award className="h-3 w-3 mr-1" />
                      {winners.length > 1 ? `Tie · ${winners.length} Winners` : 'Winner Assigned'}
                    </Badge>
                  )}
                </div>
                {prize.description && (
                  <p className="text-sm text-muted-foreground mb-2">{prize.description}</p>
                )}
                <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Trophy className="h-4 w-4 flex-shrink-0" />
                    <span className="truncate">{prize.tournament.name}</span>
                  </div>
                  {prize.bracket && (
                    <div className="flex items-center gap-1">
                      <Users className="h-4 w-4 flex-shrink-0" />
                      {prize.bracket.name}
                    </div>
                  )}
                  {prize.group && (
                    <div className="flex items-center gap-1">
                      <Users className="h-4 w-4 flex-shrink-0" />
                      {prize.group.name}
                    </div>
                  )}
                  {hasWinner && (
                    <div className="flex items-center gap-1">
                      <Award className="h-4 w-4 flex-shrink-0" />
                      {winners.length > 1 ? 'Winners' : 'Winner'}: {formatWinnerNames(winners)}
                    </div>
                  )}
                </div>
              </div>
              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onAssignWinner(prize)}
                  className="flex-1 sm:flex-none"
                >
                  <Award className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">{hasWinner ? 'Change Winners' : 'Assign Winners'}</span>
                  <span className="sm:hidden">{hasWinner ? 'Change' : 'Assign'}</span>
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onEdit(prize)}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                {!hasWinner && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onDelete(prize.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        )
      })}
    </div>
  )
}
